// part of nuxt
import type { $Fetch, NitroFetchRequest } from "nitropack"

/**
 * Escapes a string for safe use inside Typesense filter_by backtick-quoted values.
 * Backticks in the value are doubled so they don't break the filter syntax.
 */
function escapeTypesenseFilterValue(value: string): string {
  return value.replace(/`/g, "``")
}

export function jobRepository<T>(fetch: $Fetch<T, NitroFetchRequest>) {
  return {
    /**
     * Returns job details from Typesense
     * @param tenantId targeted tenant
     * @param jobShopId id of the jobshop
     * @param jobId id of the job
     * @param typesenseApiKey scoped API key for Typesense
     * @param typesenseUrl URL of the Typesense instance
     * @returns
     */
    async getJobById(tenantId: string, jobShopId: string, jobId: string, typesenseApiKey: string, typesenseUrl: string): Promise<FlatJob> {
      const response = await fetch<TypesenseResponse>(`${typesenseUrl}/multi_search`, {
        method: "POST",
        headers: {
          "X-Tenant-Id": tenantId,
          "X-JobShop-Id": jobShopId,
          "X-Typesense-Api-Key": typesenseApiKey,
        },
        body: JSON.stringify({
          searches: [{
            collection: "offers",
            q: "*",
            filter_by: `offer_uuid:=\`${escapeTypesenseFilterValue(jobId)}\``,
            limit: 1,
          }],
        }),
      })

      const hit = response.results?.[0]?.hits?.[0]
      if (!hit) {
        throw createError({
          statusCode: 404,
          statusMessage: "Job not found",
        })
      }

      return hit.document as FlatJob
    },

    /**
     * Returns multiple jobs from Typesense by their external reference IDs
     * @param tenantId targeted tenant
     * @param jobShopId id of the jobshop
     * @param externalReferenceIds array of external reference IDs
     * @param typesenseApiKey scoped API key for Typesense
     * @param typesenseUrl URL of the Typesense instance
     * @returns
     */
    async getJobsByExternalReferenceIds(tenantId: string, jobShopId: string, externalReferenceIds: string[], typesenseApiKey: string, typesenseUrl: string): Promise<FlatJob[]> {
      const filterBy = `external_reference_id:=[${externalReferenceIds.map(id => `\`${escapeTypesenseFilterValue(id)}\``).join(",")}]`

      const response = await fetch<TypesenseResponse>(`${typesenseUrl}/multi_search`, {
        method: "POST",
        headers: {
          "X-Tenant-Id": tenantId,
          "X-JobShop-Id": jobShopId,
          "X-Typesense-Api-Key": typesenseApiKey,
        },
        body: JSON.stringify({
          searches: [{
            collection: "offers",
            q: "*",
            filter_by: filterBy,
            limit: externalReferenceIds.length,
            exclude_fields: "title_embed",
          }],
        }),
      })

      const hits = response.results?.[0]?.hits ?? []
      return hits.map(hit => hit.document as FlatJob)
    },
  }
}
